/**
 * Roadmap #16 + #17 — the Facility Editor's Cameras tab.
 *
 * Optional configuration only: which cameras the shop has, where they are,
 * what they can do, and what each one can see. Nothing here connects to a
 * camera, reads a plate, or moves a vehicle; the copy never claims otherwise.
 * Phone, tablet and desktop share one layout that stacks on small screens.
 *
 * Reads: cameras (1), active coverage (1), mappable bays + spots (2).
 * Writes: the server authorities only (shop managers). No realtime channel.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Camera, Loader2, MapPin, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  archiveShopCamera, cameraRefusalMessage, readCameraTargets, readShopCameraCoverage, readShopCameras,
  saveShopCamera, setCameraCoverage, type CameraDraft, type CameraTargets,
} from '@/lib/cameras/api';
import {
  NO_CONNECTION_WORDS, TOPOLOGY_WORDS, cameraEvidenceState, capabilityWords, coverageIsValid, coverageKey,
  coverageSummary, coverageTargetLabel, deriveCameraTopology, spotRange, suggestedCoverageKinds,
  type CameraCoverageRow, type CameraTargetIndex, type CoverageTarget, type ShopCamera,
} from '@/lib/cameras/model';
import { CoverageMap } from './CoverageMap';

const EMPTY_DRAFT: CameraDraft = {
  name: '', placement: 'INTERIOR', observesOccupancy: true, observesTransition: false, readsPlates: false, enabled: true,
};

function when(iso: string | null): string | null {
  return iso ? new Date(iso).toLocaleString() : null;
}

export function CameraManager({ shopId }: { shopId: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const camerasQ = useQuery({ queryKey: ['shop-cameras', shopId], queryFn: () => readShopCameras(shopId) });
  const coverageQ = useQuery({ queryKey: ['camera-coverage', shopId], queryFn: () => readShopCameraCoverage(shopId) });
  const targetsQ = useQuery({ queryKey: ['camera-targets', shopId], queryFn: () => readCameraTargets(shopId) });

  const [formFor, setFormFor] = useState<ShopCamera | 'new' | null>(null);
  const [coverageFor, setCoverageFor] = useState<ShopCamera | null>(null);
  const [archiveFor, setArchiveFor] = useState<ShopCamera | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['shop-cameras', shopId] });
    void qc.invalidateQueries({ queryKey: ['camera-coverage', shopId] });
  };
  const refused = (reason: string) =>
    toast({ title: 'Camera not saved', description: cameraRefusalMessage(reason), variant: 'destructive' });

  const toggleEnabled = useMutation({
    mutationFn: (c: ShopCamera) => saveShopCamera(shopId, c.id, {
      name: c.name, placement: c.placement, observesOccupancy: c.observesOccupancy,
      observesTransition: c.observesTransition, readsPlates: c.readsPlates, enabled: !c.enabled,
    }),
    onSuccess: (r) => { if (!r.ok) refused(r.reason); refresh(); },
  });
  const archive = useMutation({
    mutationFn: (c: ShopCamera) => archiveShopCamera(c.id),
    onSuccess: (r) => {
      if (!r.ok) refused(r.reason); else toast({ title: 'Camera archived' });
      setArchiveFor(null);
      refresh();
    },
  });

  const cameras = camerasQ.data ?? [];
  const active = cameras.filter((c) => !c.archivedAt);
  const archived = cameras.filter((c) => c.archivedAt);
  const coverageByCamera = useMemo(() => {
    const m = new Map<string, CameraCoverageRow[]>();
    for (const r of coverageQ.data ?? []) m.set(r.cameraId, [...(m.get(r.cameraId) ?? []), r]);
    return m;
  }, [coverageQ.data]);
  const targetIndex = useMemo<CameraTargetIndex>(() => ({
    bayLabels: new Map((targetsQ.data?.bays ?? []).map((b) => [b.id, b.label])),
    spotLabels: new Map((targetsQ.data?.spots ?? []).map((s) => [s.id, s.label])),
  }), [targetsQ.data]);

  if (camerasQ.isLoading || coverageQ.isLoading || targetsQ.isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (camerasQ.error || coverageQ.error || targetsQ.error) {
    return (
      <div className="p-6 text-sm text-destructive" role="alert" data-testid="camera-load-error">
        Cameras could not be loaded. Nothing else in OptiBay depends on them — the rest of the shop works normally.
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto" data-testid="camera-manager">
      <div className="mx-auto w-full max-w-4xl space-y-4 p-4 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">Cameras</h2>
            <p className="text-sm text-muted-foreground">
              Cameras are optional. OptiBay works normally without them.
            </p>
            <p className="text-sm text-muted-foreground">
              Map what each camera can see so future vehicle observations can be interpreted correctly.
              Cameras are not connected to OptiBay yet, and nothing here reads plates or moves vehicles.
            </p>
          </div>
          <Button className="min-h-11 shrink-0" onClick={() => setFormFor('new')} data-testid="camera-add">
            <Plus className="mr-1 h-4 w-4" /> Add camera
          </Button>
        </div>

        <div className="text-xs text-muted-foreground" data-testid="camera-topology">
          {TOPOLOGY_WORDS[deriveCameraTopology(cameras)]}
        </div>

        {active.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="camera-empty">
            <Camera className="mx-auto mb-2 h-6 w-6" />
            No cameras configured. That's fine — every part of OptiBay works without them.
          </div>
        ) : (
          <ul className="space-y-3">
            {active.map((c) => {
              const rows = coverageByCamera.get(c.id) ?? [];
              const stale = rows.some((r) => !coverageIsValid(r, targetIndex));
              return (
                <li key={c.id} className="rounded-lg border bg-card p-4" data-testid={`camera-card-${c.id}`}>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-1">
                      <div className="break-words font-medium">{c.name}</div>
                      <div className="flex flex-wrap gap-1.5 text-xs">
                        <Badge variant="outline">{c.placement === 'INTERIOR' ? 'Interior' : 'Exterior'}</Badge>
                        <Badge variant={c.enabled ? 'secondary' : 'outline'} data-testid={`camera-enabled-${c.id}`}>
                          {c.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">{capabilityWords(c)}</div>
                      <div className={cn('break-words text-sm', stale && 'text-amber-700 dark:text-amber-400')}
                           data-testid={`camera-coverage-summary-${c.id}`}>
                        {coverageSummary(rows, targetIndex)}
                      </div>
                      <div className="text-xs text-muted-foreground" data-testid={`camera-evidence-${c.id}`}>
                        {cameraEvidenceState(c) === 'NO_DATA' ? NO_CONNECTION_WORDS : (
                          <span className="space-x-2">
                            {c.lastSeenAt && <span>Last seen {when(c.lastSeenAt)}</span>}
                            {c.lastObservationAt && <span>Last observation {when(c.lastObservationAt)}</span>}
                            {c.lastErrorAt && <span>Last error {when(c.lastErrorAt)}{c.lastError ? ` — ${c.lastError}` : ''}</span>}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 sm:justify-end">
                      <Button variant="outline" className="min-h-11" onClick={() => setCoverageFor(c)} data-testid={`camera-map-${c.id}`}>
                        <MapPin className="mr-1 h-4 w-4" /> Coverage
                      </Button>
                      <Button variant="outline" className="min-h-11" onClick={() => setFormFor(c)} data-testid={`camera-edit-${c.id}`}>
                        <Pencil className="mr-1 h-4 w-4" /> Edit
                      </Button>
                      <Button variant="outline" className="min-h-11" disabled={toggleEnabled.isPending}
                              onClick={() => toggleEnabled.mutate(c)} data-testid={`camera-toggle-${c.id}`}>
                        {c.enabled ? 'Disable' : 'Enable'}
                      </Button>
                      <Button variant="outline" className="min-h-11" onClick={() => setArchiveFor(c)} data-testid={`camera-archive-${c.id}`}>
                        <Archive className="mr-1 h-4 w-4" /> Archive
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {archived.length > 0 && (
          <details className="rounded-lg border p-4 text-sm" data-testid="camera-archived">
            <summary className="cursor-pointer select-none font-medium">Archived cameras ({archived.length})</summary>
            <p className="mt-2 text-muted-foreground">Kept for history. Archived cameras are not part of the shop's camera setup.</p>
            <ul className="mt-2 space-y-1">
              {archived.map((c) => (
                <li key={c.id} className="break-words text-muted-foreground">
                  {c.name} · {c.placement === 'INTERIOR' ? 'Interior' : 'Exterior'} · archived {when(c.archivedAt)}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>

      {formFor && (
        <CameraFormDialog
          shopId={shopId}
          camera={formFor === 'new' ? null : formFor}
          onClose={() => setFormFor(null)}
          onSaved={() => { setFormFor(null); refresh(); }}
          onRefused={refused}
        />
      )}
      {coverageFor && targetsQ.data && (
        <CoverageDialog
          camera={coverageFor}
          rows={coverageByCamera.get(coverageFor.id) ?? []}
          targets={targetsQ.data}
          targetIndex={targetIndex}
          onClose={() => setCoverageFor(null)}
          onSaved={() => { setCoverageFor(null); refresh(); }}
          onRefused={refused}
        />
      )}
      <AlertDialog open={!!archiveFor} onOpenChange={(o) => { if (!o) setArchiveFor(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {archiveFor?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The camera leaves the shop's camera setup and can't be edited again. It is kept, with what it covered,
              so its history stays understandable. To pause a camera you still have, disable it instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep camera</AlertDialogCancel>
            <AlertDialogAction onClick={() => archiveFor && archive.mutate(archiveFor)} data-testid="camera-archive-confirm">
              Archive camera
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Add / edit ────────────────────────────────────────────────────────────
function CameraFormDialog({ shopId, camera, onClose, onSaved, onRefused }: {
  shopId: string; camera: ShopCamera | null; onClose: () => void; onSaved: () => void; onRefused: (r: string) => void;
}) {
  const [d, setD] = useState<CameraDraft>(camera ? {
    name: camera.name, placement: camera.placement, observesOccupancy: camera.observesOccupancy,
    observesTransition: camera.observesTransition, readsPlates: camera.readsPlates, enabled: camera.enabled,
  } : EMPTY_DRAFT);
  const save = useMutation({
    mutationFn: () => saveShopCamera(shopId, camera?.id ?? null, d),
    onSuccess: (r) => { if (r.ok) onSaved(); else onRefused(r.reason); },
  });
  const valid = d.name.trim().length > 0 && d.name.trim().length <= 80
    && (d.observesOccupancy || d.observesTransition || d.readsPlates);
  const cap = (key: 'observesOccupancy' | 'observesTransition' | 'readsPlates', label: string, hint: string) => (
    <label className="flex min-h-11 cursor-pointer items-start gap-3 rounded-md border p-3">
      <Checkbox checked={d[key]} onCheckedChange={(v) => setD({ ...d, [key]: v === true })} data-testid={`camera-cap-${key}`} />
      <span className="space-y-0.5">
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </span>
    </label>
  );
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{camera ? 'Edit camera' : 'Add camera'}</DialogTitle>
          <DialogDescription>
            Describe the camera. No network address or password is needed — cameras are not connected yet.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="camera-name">Camera name</Label>
            <Input id="camera-name" value={d.name} maxLength={80} placeholder="e.g. Back Lot"
                   onChange={(e) => setD({ ...d, name: e.target.value })} data-testid="camera-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Placement</Label>
            <RadioGroup value={d.placement} onValueChange={(v) => setD({ ...d, placement: v as CameraDraft['placement'] })}
                        className="grid grid-cols-2 gap-2">
              {(['INTERIOR', 'EXTERIOR'] as const).map((p) => (
                <label key={p} className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md border p-3">
                  <RadioGroupItem value={p} data-testid={`camera-placement-${p}`} />
                  <span className="text-sm">{p === 'INTERIOR' ? 'Interior' : 'Exterior'}</span>
                </label>
              ))}
            </RadioGroup>
          </div>
          <div className="space-y-1.5">
            <Label>What can this camera do?</Label>
            {cap('observesOccupancy', 'Vehicle / space occupancy', 'Can see whether a vehicle is in a bay, spot or lot area.')}
            {cap('observesTransition', 'Entry / exit transitions', 'Can see vehicles arriving or leaving through a lane or gate.')}
            {cap('readsPlates', 'License plate reading', 'The hardware can read plates. Plate reading is not active in OptiBay yet.')}
          </div>
          <label className="flex min-h-11 items-center justify-between gap-3 rounded-md border p-3">
            <span className="space-y-0.5">
              <span className="block text-sm font-medium">Enabled</span>
              <span className="block text-xs text-muted-foreground">A disabled camera keeps its setup but would be ignored by future automation.</span>
            </span>
            <Switch checked={d.enabled} onCheckedChange={(v) => setD({ ...d, enabled: v })} data-testid="camera-enabled-switch" />
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="min-h-11" onClick={onClose}>Cancel</Button>
          <Button className="min-h-11" disabled={!valid || save.isPending} onClick={() => save.mutate()} data-testid="camera-save">
            {save.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {camera ? 'Save camera' : 'Add camera'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Coverage ──────────────────────────────────────────────────────────────
function CoverageDialog({ camera, rows, targets, targetIndex, onClose, onSaved, onRefused }: {
  camera: ShopCamera; rows: readonly CameraCoverageRow[]; targets: CameraTargets; targetIndex: CameraTargetIndex;
  onClose: () => void; onSaved: () => void; onRefused: (r: string) => void;
}) {
  const [bayIds, setBayIds] = useState(() => new Set(rows.filter((r) => r.kind === 'BAY').map((r) => r.bayId!)));
  const [spotIds, setSpotIds] = useState(() => new Set(rows.filter((r) => r.kind === 'PARKING_SPOT').map((r) => r.parkingSpotId!)));
  const [areas, setAreas] = useState<string[]>(() => rows.filter((r) => r.kind === 'LOT_AREA').map((r) => r.label!));
  const [gates, setGates] = useState<Array<{ label: string; entrance: boolean; exit: boolean }>>(() => {
    const m = new Map<string, { label: string; entrance: boolean; exit: boolean }>();
    for (const r of rows) {
      if (r.kind !== 'ENTRANCE' && r.kind !== 'EXIT') continue;
      const k = r.label!.trim().toLowerCase();
      const g = m.get(k) ?? { label: r.label!.trim(), entrance: false, exit: false };
      if (r.kind === 'ENTRANCE') g.entrance = true; else g.exit = true;
      m.set(k, g);
    }
    return [...m.values()];
  });
  const [newArea, setNewArea] = useState('');
  const [newGate, setNewGate] = useState('');
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [showAll, setShowAll] = useState(false);

  const suggested = suggestedCoverageKinds(camera);
  const show = (k: CoverageTarget['kind']) => showAll || suggested.includes(k);
  const toggle = (set: Set<string>, setter: (s: Set<string>) => void) => (id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };
  const staleRows = rows.filter((r) => (r.kind === 'BAY' || r.kind === 'PARKING_SPOT') && !coverageIsValid(r, targetIndex));
  const orderedSpotIds = targets.spots.map((s) => s.id);

  const desired: CoverageTarget[] = [
    ...[...bayIds].map((id) => ({ kind: 'BAY' as const, bayId: id })),
    ...[...spotIds].map((id) => ({ kind: 'PARKING_SPOT' as const, parkingSpotId: id })),
    ...areas.map((label) => ({ kind: 'LOT_AREA' as const, label })),
    ...gates.flatMap((g) => [
      ...(g.entrance ? [{ kind: 'ENTRANCE' as const, label: g.label }] : []),
      ...(g.exit ? [{ kind: 'EXIT' as const, label: g.label }] : []),
    ]),
  ];
  const unique = [...new Map(desired.map((t) => [coverageKey(t), t])).values()];
  const save = useMutation({
    mutationFn: () => setCameraCoverage(camera.id, unique),
    onSuccess: (r) => { if (r.ok) onSaved(); else onRefused(r.reason); },
  });

  const addArea = () => {
    const v = newArea.trim();
    if (v && !areas.some((a) => a.trim().toLowerCase() === v.toLowerCase())) setAreas([...areas, v]);
    setNewArea('');
  };
  const addGate = () => {
    const v = newGate.trim();
    if (v && !gates.some((g) => g.label.toLowerCase() === v.toLowerCase())) setGates([...gates, { label: v, entrance: true, exit: true }]);
    setNewGate('');
  };

  const checkRow = (id: string, label: string, checked: boolean, onToggle: () => void, testid: string) => (
    <label key={id} className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md border px-3 py-2">
      <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={testid} />
      <span className="min-w-0 break-words text-sm">{label}</span>
    </label>
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="flex max-h-[95dvh] w-[calc(100vw-1rem)] max-w-4xl flex-col overflow-hidden p-0" data-testid="coverage-dialog">
        <DialogHeader className="border-b p-4">
          <DialogTitle className="break-words">What can {camera.name} see?</DialogTitle>
          <DialogDescription>
            Coverage says what this camera is able to observe — not what is parked there. A place no camera covers is
            simply unobserved, never assumed empty.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
          {staleRows.length > 0 && (
            <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm" data-testid="coverage-stale">
              <p className="font-medium">Some coverage is no longer available</p>
              <ul className="mt-1 space-y-1">
                {staleRows.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2">
                    <span>{coverageTargetLabel(r, targetIndex)}</span>
                    <Button variant="ghost" size="sm" className="min-h-11" data-testid={`coverage-stale-remove-${r.id}`}
                      onClick={() => r.kind === 'BAY' ? toggle(bayIds, setBayIds)(r.bayId!) : toggle(spotIds, setSpotIds)(r.parkingSpotId!)}>
                      <Trash2 className="mr-1 h-4 w-4" /> Remove
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(show('BAY') || show('PARKING_SPOT')) && (
            <CoverageMap
              bays={show('BAY') ? targets.bays : []}
              spots={show('PARKING_SPOT') ? targets.spots : []}
              selectedBayIds={bayIds}
              selectedSpotIds={spotIds}
              onToggleBay={toggle(bayIds, setBayIds)}
              onToggleSpot={toggle(spotIds, setSpotIds)}
            />
          )}

          {show('BAY') && targets.bays.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-sm font-semibold">Bays</h3>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {targets.bays.map((b) => checkRow(b.id, b.label, bayIds.has(b.id), () => toggle(bayIds, setBayIds)(b.id), `coverage-bay-${b.id}`))}
              </div>
            </section>
          )}

          {show('PARKING_SPOT') && targets.spots.length > 0 && (
            <section className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold">Parking spots <span className="font-normal text-muted-foreground">({spotIds.size} selected)</span></h3>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="min-h-11" onClick={() => setSpotIds(new Set(orderedSpotIds))} data-testid="coverage-spots-all">
                    Select all spots
                  </Button>
                  <Button variant="outline" size="sm" className="min-h-11" onClick={() => setSpotIds(new Set())} data-testid="coverage-spots-none">
                    Clear spots
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap items-end gap-2 rounded-md border p-2 text-sm">
                <span className="self-center text-muted-foreground">Add a range:</span>
                <select aria-label="From spot" className="min-h-11 rounded-md border bg-background px-2" value={rangeFrom}
                        onChange={(e) => setRangeFrom(e.target.value)} data-testid="coverage-range-from">
                  <option value="">From…</option>
                  {targets.spots.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                <select aria-label="To spot" className="min-h-11 rounded-md border bg-background px-2" value={rangeTo}
                        onChange={(e) => setRangeTo(e.target.value)} data-testid="coverage-range-to">
                  <option value="">To…</option>
                  {targets.spots.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                <Button variant="secondary" size="sm" className="min-h-11" disabled={!rangeFrom || !rangeTo} data-testid="coverage-range-add"
                        onClick={() => { setSpotIds(new Set([...spotIds, ...spotRange(orderedSpotIds, rangeFrom, rangeTo)])); setRangeFrom(''); setRangeTo(''); }}>
                  Add range
                </Button>
              </div>
              <div className="grid max-h-72 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3 lg:grid-cols-4">
                {targets.spots.map((s) => checkRow(s.id, s.label, spotIds.has(s.id), () => toggle(spotIds, setSpotIds)(s.id), `coverage-spot-${s.id}`))}
              </div>
            </section>
          )}

          {show('LOT_AREA') && (
            <section className="space-y-2">
              <h3 className="text-sm font-semibold">Lot areas</h3>
              <p className="text-xs text-muted-foreground">
                A coarse area ("South Lot", "Front Row"). It means a vehicle is somewhere in that area — not in a particular spot.
              </p>
              <ul className="flex flex-wrap gap-2">
                {areas.map((a) => (
                  <li key={a.toLowerCase()}>
                    <Badge variant="secondary" className="min-h-9 gap-1 text-sm">
                      <span className="break-all">{a}</span>
                      <button type="button" aria-label={`Remove ${a}`} className="flex h-9 w-9 items-center justify-center"
                              onClick={() => setAreas(areas.filter((x) => x !== a))}><X className="h-4 w-4" /></button>
                    </Badge>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <Input value={newArea} maxLength={80} placeholder="e.g. South Lot" onChange={(e) => setNewArea(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addArea(); } }} data-testid="coverage-area-input" />
                <Button variant="outline" className="min-h-11 shrink-0" onClick={addArea} data-testid="coverage-area-add">Add area</Button>
              </div>
            </section>
          )}

          {(show('ENTRANCE') || show('EXIT')) && (
            <section className="space-y-2">
              <h3 className="text-sm font-semibold">Entrances and exits</h3>
              <p className="text-xs text-muted-foreground">
                A lane or gate where vehicles come and go. It describes arrivals and departures — it is not a place a vehicle is parked.
              </p>
              <ul className="space-y-2">
                {gates.map((g, i) => (
                  <li key={g.label.toLowerCase()} className="flex flex-wrap items-center gap-3 rounded-md border p-2">
                    <span className="min-w-0 flex-1 break-words text-sm font-medium">{g.label}</span>
                    <label className="flex min-h-11 items-center gap-2 text-sm">
                      <Checkbox checked={g.entrance} data-testid={`coverage-gate-entrance-${i}`}
                                onCheckedChange={(v) => setGates(gates.map((x, j) => j === i ? { ...x, entrance: v === true } : x))} /> Entrance
                    </label>
                    <label className="flex min-h-11 items-center gap-2 text-sm">
                      <Checkbox checked={g.exit} data-testid={`coverage-gate-exit-${i}`}
                                onCheckedChange={(v) => setGates(gates.map((x, j) => j === i ? { ...x, exit: v === true } : x))} /> Exit
                    </label>
                    <Button variant="ghost" size="sm" className="min-h-11" aria-label={`Remove ${g.label}`}
                            onClick={() => setGates(gates.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <Input value={newGate} maxLength={80} placeholder="e.g. Front Drive" onChange={(e) => setNewGate(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGate(); } }} data-testid="coverage-gate-input" />
                <Button variant="outline" className="min-h-11 shrink-0" onClick={addGate} data-testid="coverage-gate-add">Add lane or gate</Button>
              </div>
            </section>
          )}

          {!showAll && suggested.length < 5 && (
            <Button variant="link" className="min-h-11 px-0" onClick={() => setShowAll(true)} data-testid="coverage-show-all">
              Show every coverage type
            </Button>
          )}
        </div>
        <DialogFooter className="gap-2 border-t p-4">
          <Button variant="outline" className="min-h-11" onClick={onClose}>Cancel</Button>
          <Button className="min-h-11" disabled={save.isPending} onClick={() => save.mutate()} data-testid="coverage-save">
            {save.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Save coverage
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
